import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Spinner, EmptyState, Badge } from '@/components/ui/misc';
import { Dashboard } from '@/components/dashboard/Dashboard';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';

export function Results() {
  const { runId } = useParams<{ runId: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['run', runId],
    queryFn: () => api.getAnalysis(runId!),
    enabled: Boolean(runId),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <EmptyState
        title="Result not found"
        description="This analysis may have been removed or never existed."
        action={
          <Link to="/app">
            <Button>Back to dashboard</Button>
          </Link>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/app/history"
          className="mb-3 inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary"
        >
          <ArrowLeft size={15} /> Back to history
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight text-text-primary">
            {data.run.project.brandName ?? data.run.project.websiteUrl}
          </h1>
          <Badge tone={data.run.status === 'completed' ? 'teal' : 'blue'}>
            {data.run.status === 'partially_completed' ? 'Partial results' : 'Completed'}
          </Badge>
          <span className="text-sm text-text-muted">Run {formatDate(data.run.createdAt)}</span>
        </div>
      </div>
      <Dashboard run={data.run} />
    </div>
  );
}
